import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SectionHeader, CTABanner } from "@/components/Shared";
import { Search, FileCheck2, PenLine, Users, DraftingCompass, Building2 } from "lucide-react";
import { IMAGES } from "@/data/content";

const PILLARS = [
  { icon: Search, title: "Opportunity Identification", description: "We look for existing properties and urban parcels where thoughtful redevelopment can unlock meaningful potential." },
  { icon: FileCheck2, title: "Feasibility", description: "Every opportunity is assessed carefully — location, regulations, structure and commercial viability — before any commitment is made." },
  { icon: PenLine, title: "Planning", description: "We develop clear, practical plans that balance design quality, functionality and responsible execution." },
  { icon: Users, title: "Stakeholder Coordination", description: "Redevelopment involves people. We prioritise transparent communication with societies, owners and partners at every step." },
  { icon: DraftingCompass, title: "Design Strategy", description: "We work towards design approaches that make spaces better planned, more functional and future-ready." },
  { icon: Building2, title: "Development Execution", description: "We pursue quality execution with the discipline and accountability that long-term value creation demands." },
];

const Redevelopment = () => (
  <>
    <SEO
      title="Redevelopment | Evaya Ventures — Reimagining Existing Spaces"
      description="Evaya Ventures is exploring redevelopment opportunities where existing properties can be thoughtfully transformed into better planned, more functional and future-ready spaces."
      path="/redevelopment"
      breadcrumbs={[{ name: "Home", path: "/" }, { name: "Redevelopment", path: "/redevelopment" }]}
    />

    <PageHero
      overline="Redevelopment"
      title="Reimagining Existing Spaces"
      statement="From Existing Spaces to Extraordinary Possibilities."
      description="Evaya Ventures is exploring redevelopment opportunities where existing properties can be thoughtfully transformed into better planned, more functional and future-ready spaces."
      image={IMAGES.urbanMumbai}
      imageAlt={IMAGES.urbanMumbaiAlt}
      testId="redevelopment-hero"
      dark
    />

    {/* Honest positioning */}
    <section className="py-24 lg:py-32" data-testid="redevelopment-intro">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <SectionHeader
            overline="Our Position"
            title="We identify potential, plan carefully and create better urban spaces"
            testId="redevelopment-position"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base leading-relaxed text-ink/65 md:text-lg">
              Evaya Ventures is at the beginning of its redevelopment journey. We are currently studying established urban neighbourhoods, engaging with stakeholders and evaluating where existing buildings and underutilised spaces can be thoughtfully renewed.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">
              We do not claim completed redevelopment towers. What we offer instead is a disciplined process, transparent communication and a genuine commitment to creating better urban spaces — one well-considered opportunity at a time.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.redevelopmentCard} alt={IMAGES.redevelopmentCardAlt} loading="lazy" className="h-64 w-full object-cover md:h-80" />
            <img src={IMAGES.blueprints} alt={IMAGES.blueprintsAlt} loading="lazy" className="mt-8 h-64 w-full object-cover md:h-80" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* Pillars */}
    <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="redevelopment-pillars">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeader
          overline="What Redevelopment Involves"
          title="A complete view of the redevelopment journey"
          testId="redevelopment-pillars"
        />
        <div className="mt-14 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((pillar, i) => (
            <div key={pillar.title} className="bg-white">
              <Reveal delay={i * 0.06} className="h-full p-8 lg:p-10">
                <pillar.icon className="h-7 w-7 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-5 font-display text-2xl text-ink">{pillar.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{pillar.description}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Who we work with */}
    <section className="py-24 lg:py-32" data-testid="redevelopment-stakeholders">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <Reveal className="order-2 lg:order-1 lg:col-span-5">
          <div className="relative">
            <div className="absolute -bottom-4 -left-4 h-full w-full border border-brand/40" aria-hidden="true" />
            <img src={IMAGES.construction} alt={IMAGES.constructionAlt} loading="lazy" className="relative h-[400px] w-full object-cover" />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2 lg:col-span-7 lg:pl-8">
          <SectionHeader
            overline="Who We Work With"
            title="Societies, owners and partners with a shared vision"
            description="If your housing society, property or urban parcel has redevelopment potential, we would be glad to evaluate it with you — transparently and without obligation."
            testId="redevelopment-stakeholders"
          />
          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-4" data-testid="redevelopment-stakeholder-list">
              {[
                "Housing societies considering redevelopment",
                "Owners of ageing or underutilised urban properties",
                "Landowners exploring joint-development structures",
                "Partners aligned with responsible urban renewal",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 border-b border-ink/10 pb-4 text-base text-ink/75">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-brand" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>

    <CTABanner title="Have a redevelopment opportunity?" copy="Share the details of your property or society with us. Our team will review it carefully and respond with an honest initial assessment." />
  </>
);

export default Redevelopment;
